import { type ParticipantRouteAccessId, canAccessParticipantRoute } from '@/config/route-access'
import { useAccessControl } from '@/hooks/use-access-control'
import { useNavVisibility } from '@/hooks/use-nav-visibility'
import { enableCoachRoster, enableCoaching, enableSkillsMap } from '@/lib/feature-flags'
import { cn } from '@/lib/utils'
import { useTabNotificationsStore } from '@/stores/tab-notifications.store'
import {
  Bot,
  Briefcase,
  Building2,
  CalendarDays,
  FileText,
  Heart,
  Kanban,
  Landmark,
  LifeBuoy,
  Map as MapIcon,
  type MessageCircle,
  User,
} from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'

interface Tab {
  name: string
  href: string
  icon: typeof MessageCircle
  alwaysVisible: boolean
  routeId: ParticipantRouteAccessId
  notificationKey?: 'jobs' | 'skills'
}

const OUTPLACEMENT_HREFS = new Set([
  '/resume',
  '/opportunities',
  '/tracker',
  '/resume-builder',
  '/start-a-business',
  '/retirement',
])

// Outplacement features — switched on only once severance has begun. For a
// severance-cohort member who hasn't started yet, these are hidden until their
// severance start date; regular embrace users are never "severance pending", so
// their nav is unchanged.
const tabs: Tab[] = [
  {
    name: 'Resume',
    href: '/resume',
    icon: FileText,
    alwaysVisible: true,
    routeId: 'participant.external-jobs',
  },
  {
    name: 'Career Map',
    href: '/career-map',
    icon: MapIcon,
    alwaysVisible: true,
    routeId: 'participant.skills-map',
  },
  {
    name: 'Jobs Board',
    href: '/opportunities',
    icon: Briefcase,
    alwaysVisible: true,
    routeId: 'participant.jobs',
    notificationKey: 'jobs',
  },
  {
    name: 'Agents',
    href: '/resume-builder',
    icon: Bot,
    alwaysVisible: true,
    routeId: 'participant.jobs',
  },
  {
    name: 'Tracker',
    href: '/tracker',
    icon: Kanban,
    alwaysVisible: true,
    routeId: 'participant.external-jobs',
  },
  {
    name: 'Coaching',
    href: '/coaching',
    icon: CalendarDays,
    alwaysVisible: true,
    routeId: 'participant.coaching',
  },
  {
    name: 'Start Business',
    href: '/start-a-business',
    icon: Building2,
    alwaysVisible: true,
    routeId: 'participant.start-business',
  },
  {
    name: 'Retirement',
    href: '/retirement',
    icon: Landmark,
    alwaysVisible: true,
    routeId: 'participant.retirement',
  },
  {
    name: 'Benefits',
    href: '/benefits/election',
    icon: Heart,
    alwaysVisible: false,
    routeId: 'participant.election',
  },
  {
    name: 'Benefits',
    href: '/benefits',
    icon: LifeBuoy,
    alwaysVisible: true,
    routeId: 'participant.benefits',
  },
  {
    name: 'Profile',
    href: '/profile',
    icon: User,
    alwaysVisible: true,
    routeId: 'participant.profile',
  },
]

export function BottomNav() {
  const location = useLocation()
  const { isSeveranceUser, severancePending } = useNavVisibility()
  const authorization = useAccessControl()
  const notifications = useTabNotificationsStore()
  const isWorkflowRestricted = authorization.data?.participant?.workflow_restricted === true

  const visibleTabs = tabs.filter((tab) => {
    if (!authorization.data || !canAccessParticipantRoute(authorization.data, tab.routeId)) {
      return false
    }
    if (tab.href === '/coaching' && !enableCoaching) return false
    if (
      (tab.href === '/start-a-business' || tab.href === '/retirement') &&
      (!enableCoaching || !enableCoachRoster)
    )
      return false
    if (tab.href === '/career-map' && !enableSkillsMap) return false
    // The election tab only appears for severance-cohort members.
    if (tab.href === '/benefits/election') {
      return Boolean(isSeveranceUser && isWorkflowRestricted)
    }
    // Before severance starts, hide the outplacement features.
    if (severancePending && OUTPLACEMENT_HREFS.has(tab.href)) return false
    if (tab.alwaysVisible) return true
    if (tab.href === '/opportunities') return true
    if (tab.href === '/resume-builder') return true
    return true
  })

  return (
    <nav className="border-t bg-background flex items-center justify-around h-14 shrink-0 pb-safe">
      {visibleTabs.map((tab) => {
        const Icon = tab.icon
        const label =
          tab.href === '/benefits/election' && isWorkflowRestricted ? 'Benefits' : tab.name
        const isActive =
          location.pathname === tab.href || location.pathname.startsWith(`${tab.href}/`)
        const hasNotification = tab.notificationKey && notifications[tab.notificationKey]

        return (
          <Link
            key={tab.href}
            to={tab.href}
            onClick={() => {
              if (tab.notificationKey === 'jobs') {
                notifications.setJobsNotification(false)
              }
              if (tab.notificationKey === 'skills') {
                notifications.setSkillsNotification(false)
              }
            }}
            className={cn(
              'flex flex-col items-center justify-center gap-0.5 flex-1 h-full relative',
              isActive ? 'text-blue-600' : 'text-muted-foreground',
            )}
          >
            <div className="relative">
              <Icon className="h-5 w-5" />
              {hasNotification && (
                <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-blue-600 animate-pulse" />
              )}
            </div>
            <span className="text-[10px] font-medium">{label}</span>
          </Link>
        )
      })}
    </nav>
  )
}
